import { Link, useNavigate } from '@tanstack/react-router';
import type { AdminUserDto, CityDto } from '@upme/contracts';
import { Badge } from '@upme/ui/components/badge';
import { Button } from '@upme/ui/components/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@upme/ui/components/table';
import type { UsersSearch } from '../../../routes/_authenticated/users/route';
import { EmptyState } from '../../../shared/components/empty-state';
import {
  BOT_STATUS_LABEL,
  BOT_STATUS_VARIANT,
  botStatusOf,
  formatDateTime,
  formatUserName,
} from '../lib/format';

interface Props {
  users: ReadonlyArray<AdminUserDto>;
  cities: ReadonlyArray<CityDto>;
  total: number;
  page: number;
  limit: number;
}

export function UsersTable({ users, cities, total, page, limit }: Props) {
  const navigate = useNavigate();
  const cityById = new Map(cities.map((c) => [c.id, c]));

  if (users.length === 0) {
    return (
      <EmptyState
        title="Пользователи не найдены"
        description="Под текущие фильтры не подошёл ни один аккаунт — попробуйте ослабить условия."
      />
    );
  }

  const from = (page - 1) * limit + 1;
  const to = from + users.length - 1;
  const hasPrev = page > 1;
  const hasNext = to < total;

  const goToPage = (next: number) =>
    navigate({
      to: '/users',
      // TanStack типизирует `prev` объединением search-схем всех роутов;
      // сужаем до нашей — лишние ключи роутер всё равно срежет по validateSearch.
      search: (prev) => ({ ...(prev as UsersSearch), page: next }),
    });

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Пользователь</TableHead>
            <TableHead>Telegram ID</TableHead>
            <TableHead>Город</TableHead>
            <TableHead>Регистрация</TableHead>
            <TableHead>Последний визит</TableHead>
            <TableHead>Бот</TableHead>
            <TableHead>Пригласил</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((u) => {
            const city = u.cityId ? cityById.get(u.cityId) : undefined;
            const botStatus = botStatusOf(u);
            return (
              <TableRow key={u.id}>
                <TableCell>
                  <Link to="/users/$userId" params={{ userId: u.id }} className="hover:underline">
                    {u.username ? `@${u.username}` : formatUserName(u)}
                  </Link>
                  <div className="text-xs text-muted-foreground">{formatUserName(u)}</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {u.adminRoles.map((role) => (
                      <Badge key={role} variant="secondary">
                        {role === 'CITY_MODERATOR' && u.moderatedCityIds.length > 0
                          ? `${role}: ${u.moderatedCityIds.map((id) => cityById.get(id)?.name ?? '?').join(', ')}`
                          : role}
                      </Badge>
                    ))}
                    {u.isPremium && <Badge variant="outline">premium</Badge>}
                    {u.isFake && <Badge variant="outline">фейк</Badge>}
                    {u.isVip && <Badge variant="outline">VIP</Badge>}
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs">{u.telegramId ?? '—'}</TableCell>
                <TableCell>{city?.name ?? '—'}</TableCell>
                <TableCell className="tabular-nums">{formatDateTime(u.createdAt)}</TableCell>
                <TableCell className="tabular-nums">{formatDateTime(u.lastSeenAt)}</TableCell>
                <TableCell>
                  <Badge variant={BOT_STATUS_VARIANT[botStatus]}>{BOT_STATUS_LABEL[botStatus]}</Badge>
                </TableCell>
                <TableCell>
                  {u.referredBy ? (
                    <Link
                      to="/users/$userId"
                      params={{ userId: u.referredBy.id }}
                      className="hover:underline"
                    >
                      {u.referredBy.username ? `@${u.referredBy.username}` : (u.referredBy.firstName ?? '—')}
                    </Link>
                  ) : (
                    '—'
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground tabular-nums">
          {from}–{to} из {total}
        </span>
        <div className="flex gap-2">
          <Button variant="outline" disabled={!hasPrev} onClick={() => goToPage(page - 1)}>
            Назад
          </Button>
          <Button variant="outline" disabled={!hasNext} onClick={() => goToPage(page + 1)}>
            Вперёд
          </Button>
        </div>
      </div>
    </div>
  );
}
